import { useState } from "react";
import useSWR from "swr";

import { api, PUBLIC_API_URL, UserMe } from "./api";
import { Account } from "./pages/Account";
import { Admin } from "./pages/Admin";
import { Contact } from "./pages/Contact";
import { Dashboard } from "./pages/Dashboard";
import { Export } from "./pages/Export";
import { Indices } from "./pages/Indices";
import { Login } from "./pages/Login";
import { Pricing } from "./pages/Pricing";
import { Terms } from "./pages/Terms";
import { Tokens } from "./pages/Tokens";
import { Usage } from "./pages/Usage";
import { Watchlists } from "./pages/Watchlists";
import { Icon, IconName, PageHeader } from "./ui";
import { useTheme } from "./useTheme";

const fetcher = <T,>(path: string) => api.get<T>(path);

const PAGES: Record<string, (user: UserMe) => React.ReactNode> = {
  "/": (u) => <Dashboard user={u} />,
  "/tokens": () => <Tokens />,
  "/indices": (u) => <Indices user={u} />,
  "/watchlists": (u) => <Watchlists user={u} />,
  "/export": (u) => <Export user={u} />,
  "/usage": () => <Usage />,
  "/account": (u) => <Account user={u} />,
  "/admin": (u) =>
    u.permissions.includes("nh.admin") ? <Admin /> : <PageHeader title="Access denied" description="You do not have admin permissions." />,
};

export default function App() {
  // Links are plain <a href>, so every navigation is a full load; no history listener needed.
  const path = window.location.pathname;
  const { data: user, error, isLoading, mutate } = useSWR<UserMe>("/users/me", fetcher, { shouldRetryOnError: false });
  const me = error ? null : (user ?? null);
  // Only the first load shows "Loading…". SWR re-fetches /users/me whenever the window regains
  // focus; for a signed-out visitor that is a load with no data, and swapping the page out would
  // unmount it and wipe whatever they had typed.
  const loading = isLoading && !error;
  const onLoggedOut = () => mutate(undefined, { revalidate: false });

  let page: React.ReactNode;
  if (loading) page = <p className="text-muted">Loading…</p>;
  else if (path === "/terms") page = <Terms />;
  else if (path === "/pricing") page = <Pricing user={me} />;
  else if (path === "/contact") page = <Contact user={me} />;
  else if (!me) page = <Login />;
  else page = (PAGES[path] ?? notFound)(me);

  return (
    <Shell user={loading ? null : me} path={path} onLoggedOut={onLoggedOut}>
      {page}
    </Shell>
  );
}

function notFound() {
  return <PageHeader title="Not found" description={<a href="/">Back to the dashboard</a>} />;
}

// ── Shell ────────────────────────────────────────────────────

interface NavItem {
  href: string;
  label: string;
  icon?: IconName;
  children?: NavItem[];
  external?: boolean;
}

function navFor(user: UserMe): NavItem[] {
  return [
    { href: "/", label: "Dashboard", icon: "grid" },
    { href: "/usage", label: "Usage", icon: "bars" },
    { href: "/tokens", label: "API tokens", icon: "key" },
    {
      href: "/indices",
      label: "Data",
      icon: "chart",
      children: [
        { href: "/indices", label: "Indices" },
        { href: "/watchlists", label: "Watchlists" },
        { href: "/export", label: "Export" },
      ],
    },
    {
      href: "/account",
      label: "Account",
      icon: "user",
      children: [
        { href: "/account", label: "Profile" },
        { href: "/pricing", label: "Plans" },
      ],
    },
    ...(user.permissions.includes("nh.admin") ? [{ href: "/admin", label: "Admin", icon: "shield" as const }] : []),
  ];
}

const RESOURCES: NavItem[] = [
  { href: `${PUBLIC_API_URL}/docs`, label: "API docs", icon: "external", external: true },
];

interface ShellProps {
  user: UserMe | null;
  path: string;
  onLoggedOut: () => void;
  children: React.ReactNode;
}

function Shell({ user, path, onLoggedOut, children }: ShellProps) {
  const { theme, toggle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // server clears cookie either way
    }
    onLoggedOut();
    window.location.href = "/";
  };

  const sidebar = user && <Sidebar user={user} path={path} onLogout={logout} />;

  return (
    <div className="flex flex-col min-h-screen max-w-[1320px] mx-auto px-8 max-sm:px-4">
      <header className="flex items-center justify-between gap-4 h-20 max-sm:h-16 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <a href="/" className="text-lg max-sm:text-base font-extrabold tracking-tight uppercase whitespace-nowrap text-main">Nilsson Hedge</a>
          <a href="https://factor2s.ai" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-muted max-sm:hidden">
            <span className="text-[11px]">powered by</span>
            <img src="/factor2s-logo.png" alt="Factor2S" className="h-9 w-auto object-contain" />
          </a>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="flex items-center justify-center w-9 h-9 rounded-md cursor-pointer text-muted hover:text-main hover:bg-stat"
            onClick={toggle}
            title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
          >
            <Icon name={theme === "dark" ? "sun" : "moon"} />
          </button>
          {!user && ["/pricing", "/terms", "/contact"].includes(path) && (
            <a href="/" className="btn-default text-main">Sign in</a>
          )}
          {user && (
            <a
              href="/account"
              title={user.email}
              className="flex items-center justify-center w-9 h-9 rounded-full bg-accent/15 text-accent text-sm font-bold uppercase max-lg:hidden"
            >
              {(user.name ?? user.email)[0]}
            </a>
          )}
          {user && (
            <button
              className="flex lg:hidden items-center justify-center w-9 h-9 rounded-md cursor-pointer text-muted hover:text-main hover:bg-stat"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <Icon name="menu" />
            </button>
          )}
        </div>
      </header>

      {/* Mobile drawer */}
      {menuOpen && sidebar && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden animate-fade-in" onClick={() => setMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] overflow-y-auto p-4 bg-surface border-r border-border lg:hidden animate-fade-in">
            {sidebar}
          </div>
        </>
      )}

      <div className="flex-1 flex gap-12 pt-4 pb-16">
        {sidebar && <aside className="w-60 shrink-0 max-lg:hidden">{sidebar}</aside>}
        <main className="flex-1 min-w-0">{children}</main>
      </div>

      <footer className="flex flex-wrap justify-center gap-x-3 py-6 text-xs text-muted border-t border-border">
        <span>© {new Date().getFullYear()} Factor2S</span>
        <a href="/pricing" className="hover:text-main transition-colors">Plans</a>
        <a href="/terms" className="hover:text-main transition-colors">Terms</a>
        <a href="/contact" className="hover:text-main transition-colors">Support</a>
      </footer>
    </div>
  );
}

function Sidebar({ user, path, onLogout }: { user: UserMe; path: string; onLogout: () => void }) {
  return (
    <nav className="flex flex-col gap-1 text-[15px]">
      {navFor(user).map((item) => (
        <div key={item.label} className="flex flex-col gap-1">
          <NavLink item={item} active={!item.children && path === item.href} />
          {item.children?.map((c) => <NavLink key={c.href} item={c} active={path === c.href} child />)}
        </div>
      ))}

      <p className="mt-8 mb-1 px-3 text-xs font-medium text-muted">Resources</p>
      {RESOURCES.map((r) => <NavLink key={r.href} item={r} active={false} />)}

      <div className="flex flex-col gap-1 mt-6 pt-4 border-t border-border">
        <div className="px-3 py-1 min-w-0">
          <p className="text-xs text-muted">Signed in as</p>
          <p className="text-sm text-main truncate" title={user.email}>{user.email}</p>
        </div>
        <button
          onClick={onLogout}
          className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer font-medium text-left text-main hover:bg-stat"
        >
          <Icon name="logout" className="w-5 h-5 text-muted" />
          Log out
        </button>
      </div>
    </nav>
  );
}

function NavLink({ item, active, child = false }: { item: NavItem; active: boolean; child?: boolean }) {
  return (
    <a
      href={item.href}
      {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      aria-current={active ? "page" : undefined}
      className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${child ? "ml-8 font-normal" : "font-medium"} ${
        active ? "bg-stat text-accent" : "text-main hover:bg-stat"
      }`}
    >
      {item.icon && <Icon name={item.icon} className={`w-5 h-5 ${active ? "text-accent" : "text-muted"}`} />}
      {item.label}
    </a>
  );
}
